/* eslint-disable @typescript-eslint/naming-convention */
import { Component, useEffect } from 'react'
import { Link, useOutletContext, useParams, useNavigate } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Form from 'react-bootstrap/Form'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import Tooltip from 'react-bootstrap/Tooltip'

import AccessDeniedElement from '../../../components/AccessDeniedElement.jsx'
import AddGroupRolesModal from './components/AddGroupRolesModal.jsx'
import AddGroupUsersModal from './components/AddGroupUsersModal.jsx'
import AdministrationTabs from '../components/AdministrationTabs.jsx'
import ChangeParentModal from './components/ChangeParentModal.jsx'
import ConfirmationModal from '../../../components/ConfirmationModal.jsx'
import Icon from '../../../components/Icon.jsx'
import Loadable from '../../../components/Loadable.jsx'
import LoadingAnimation from '../../../components/LoadingAnimation.jsx'
import PageTitle from '../../../components/PageTitle.jsx'
import TimestampWithUTCTooltip from '../../../components/TimestampWithUTCTooltip.jsx'

import { fetchGroup, updateGroup, fetchGroupUserList, fetchGroupRoleBusinessEntities, deleteGroupRoleBusinessEntity, removeUserFromGroup } from '../../../state/actions/groupActions.js'
import { addNotification, NotificationTypes, notifyException } from '../../../state/actions/notificationActions.js'
import { fetchOrganizationRoleList } from '../../../state/actions/roleActions.js'
import { fetchUserList } from '../../../state/actions/userActions.js'

import { currentBusinessEntitySignal, businessEntitiesLoadedSignal, businessEntityDictionarySignal, rootBusinessEntitySignal } from '../../../state/signals/businessEntitySignals.js'
import { groupSignal, groupLoadedSignal, groupUserListSignal, groupUserListLoadedSignal, groupRoleBusinessEntitiesSignal, groupRoleBusinessEntitiesLoadedSignal, groupErrorSignal, groupUserListErrorSignal, groupRoleBusinessEntitiesErrorSignal } from '../../../state/signals/groupSignals.js'
import { userListSignal, userListLoadedSignal } from '../../../state/signals/userSignals.js'
import { debugSignal } from '../../../state/signals/userSettingsSignals.js'

import { abridgedClone, getUserStatusText, scrollToTop } from '../../../utils/utils.js'
import useRedirectOnBeIdChange from '../../../hooks/useRedirectOnBeIdChange.js'


const GroupDetailPage = () => {

	const navigate = useNavigate()
	const { beId, groupId } = useParams()
	const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/management/groups`)
	const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData } = useOutletContext()

	const debug = debugSignal.value

	useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewGroups) {
            addNotification('You are not authorized to view groups for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

	const canEditComputed = useComputed(
		() => {
			return userPermissionsSummary?.value?.canEditGroups
		}
	)

	useEffect(() => {
		if (beIdChanged) return
		fetchGroup(groupId, config, accessToken)
		fetchGroupUserList(groupId, config, accessToken)
		fetchUserList(beId, config, accessToken, false)
		fetchOrganizationRoleList(beId, config, accessToken, true).then(
			(organizationRoleList) => {
				fetchGroupRoleBusinessEntities(beId, groupId, config, accessToken, true)
			}
		)
	}, [beId, beIdChanged, groupId, config, accessToken])

	const showChangeParentModalSignal = useSignal(false)
	const showAddRolesModalSignal = useSignal(false)
	const showAddUsersModalSignal = useSignal(false)
	const showDeleteRoleBusinessEntityModalSignal = useSignal(false)
	const roleBusinessEntitySignal = useSignal(null)
	const showRemoveUserModalSignal = useSignal(false)
	const groupUserSignal = useSignal(null)

	const groupNameSignal = useSignal('')
	const isUpdatingSignal = useSignal(false)
	useSignalEffect(
		() => {
			if (!groupLoadedSignal.value) {
				groupNameSignal.value = 'Loading...'
			} else {
				groupNameSignal.value = groupSignal.value.name
			}
		}
	)

	const hasUsersOrBEsComputed = useComputed(
		() => {
			return groupUserListLoadedSignal.value && groupRoleBusinessEntitiesLoadedSignal.value && (groupUserListSignal.value.length > 0 || groupRoleBusinessEntitiesSignal.value.length > 0)
		}
	)
	const BusinessEntityNamesElement = ({ businessEntities }) => {
		if (!businessEntities) {
			return <>Error<br /></>
		}
		let names = []
		for (let i = 0; i < businessEntities.length; i++) {
			const businessEntity = businessEntities[i]
			names.push((<div key = {businessEntity?.id}>{businessEntity?.name}<br /></div>))
		}
		return names
	}

	const showDeleteRoleBusinessEntityModal = (roleBusinessEntity) => {
		roleBusinessEntitySignal.value = roleBusinessEntity
		showDeleteRoleBusinessEntityModalSignal.value = true
	}

	const RoleBusinessEntitiesList = () => {
		let print = false
		if (!groupRoleBusinessEntitiesLoadedSignal.value) {
			return <tr><td colSpan={3}><LoadingAnimation /></td></tr>
		}
		const roleBusinessEntities = groupRoleBusinessEntitiesSignal.value
		if (!roleBusinessEntities || roleBusinessEntities.length < 1) {
			return (
				<tr>
					<td colSpan={3} className='empty-record'>No Roles to display.</td>
				</tr>
			)
		}
		let elements = []
		for (let i = 0; i < roleBusinessEntities.length; i++) {
			const roleBusinessEntity = roleBusinessEntities[i]
			//console.log("Role business entity:")
			//console.log(roleBusinessEntity)
			const key = roleBusinessEntity.roleId
			elements.push(
				<tr key={key}>
					<td>{roleBusinessEntity.role?.name ? roleBusinessEntity.role.name : "Error: Undefined role name"}</td>
					<td>
						<BusinessEntityNamesElement businessEntities={roleBusinessEntity.businessEntities}></BusinessEntityNamesElement>
					</td>
					<td>
						<TimestampWithUTCTooltip>{roleBusinessEntity.lastUpdatedAt}</TimestampWithUTCTooltip>
					</td>
					<td className='textRight'>
						{
							userPermissionsSummary.value.canEditGroups ? (
								<label onClick={
									() => {
										showDeleteRoleBusinessEntityModal(roleBusinessEntity)
									}
								}>
								<Icon icon="x" height={40} width={40} color="#f00" />
							</label>
							) : (<span>Component locked: You do not have permission to edit users</span>)
						}
					</td>
				</tr>
			)
		}
		return elements
	}

	const showRemoveUserModal = (groupUser) => {
		groupUserSignal.value = groupUser
		if (!groupUserSignal.value) {
			throw Error("Immediately after assignment, group user signal has no value")
		}
		showRemoveUserModalSignal.value = true
	}

	const getGroupUserParentBusinessEntityName = (groupUser) => {
		if (!businessEntitiesLoadedSignal.value || !userListLoadedSignal.value) {
			return "Loading..."
		}
		if (!groupUser) {
			return "Error: Undefined group user"
		}
		if (!("parentBusinessEntityId" in groupUser)) {
			return "Error: Group user undefined BEID"
		}
		if (!businessEntityDictionarySignal.value) {
			return "Error: Empty BE dictionary"
		}
		const key = groupUser.parentBusinessEntityId
		if (!(key in businessEntityDictionarySignal.value)) {
			return `Error: Unknown BEID ${key}`
		}
		if (!("name" in businessEntityDictionarySignal.value[key])) {
			return `Error: Unnamed BEID ${groupUser.businessEntityId}`
		}
		return businessEntityDictionarySignal.value[key].name
	}

	const GroupUsersElement = () => {
		if (!groupUserListLoadedSignal.value) {
			return <tr><td colSpan={7}><LoadingAnimation /></td></tr>
		}
		if (!groupUserListSignal.value || groupUserListSignal.value.length < 1) {
			return (
				<tr>
					<td colSpan="7" className='empty-record'>No Users to display.</td>
				</tr>
			)
		}
		let users = groupUserListSignal.value
		let elements = []
		for (let i = 0; i < users.length; i++) {
			const groupUser = users[i]
			//console.log("Group user:")
			//console.log(groupUser)
			elements.push(
				<tr key={groupUser.id}>
					<td className='hidden-xs'>{groupUser.username}</td>
					<td>{groupUser.firstName}</td>
					<td>{groupUser.lastName}</td>
					<td className='hidden-xs'>{groupUser.email}</td>
					<td>{getGroupUserParentBusinessEntityName(groupUser)}</td>
					<td className='hidden-xs'>{getUserStatusText(groupUser)}</td>
					<td className='hidden-xs'>
						<TimestampWithUTCTooltip>{groupUser.lastLoginAt}</TimestampWithUTCTooltip>
					</td>
					<td>{
						userPermissionsSummary.value.canEditGroups ? (
							<label onClick={
								() => {
									showRemoveUserModal(groupUser)
								}
							}>
								<Icon icon="x" height={40} width={40} color="#f00" />
							</label>
						) : (
							<span>Component locked: You do not have permission to edit groups.</span>
						)
					}</td>
				</tr>)
		}
		return elements
	}

	const handleGroupNameChange = (event) => {
		groupNameSignal.value = event.target.value
	}

	const validGroupNameComputed = useComputed(
		() => {
			return groupNameSignal.value && typeof groupNameSignal.value == 'string' && groupNameSignal.value.length > 0 && groupNameSignal.value.length <= 50
		}
	)

	const showAddRolesModal = () => {
		showAddRolesModalSignal.value = true
	}

	const showChangeParentModal = () => {
		showChangeParentModalSignal.value = true
	}

	const showAddUsersModal = () => {
		showAddUsersModalSignal.value = true
	}

	const renderTooltip = (props) => {
		return (
			<Tooltip {...props}>Please remove all roles and users from the group before changing the parent.</Tooltip>
		)
	}

	const enableRenameGroupComputed = useComputed(
		() => {
			return groupLoadedSignal.value && !isUpdatingSignal.value && groupNameSignal.value != groupSignal.value.name && validGroupNameComputed.value
		}
	)

	const parentBusinessEntityComputed = useComputed(
		() => {
			const f = "parentBusinessEntityComputed()"
			if (!businessEntitiesLoadedSignal.value) {
				//console.log(`${f}: Business entities were not loaded`)
				return null
			}
			if (!groupLoadedSignal.value) {
				//console.log(`${f}: Group was not loaded`)
				return null
			}
			const group = groupSignal.value
			if (!("parentBusinessEntityId" in group)) {
				throw Error(`${f}: Group does not know its parent business entity ID`)
			}
			const parentId = group.parentBusinessEntityId
			if (!businessEntitiesLoadedSignal.value) {
				throw Error("Business entities were not loaded")
			}
			if (!businessEntityDictionarySignal.value) {
				throw Error("Business entity dictionary was not populated")
			}
			if (!(parentId in businessEntityDictionarySignal.value)) {
				throw Error(`${f}: Business entity with ID ${parentId} is absent from the dictionary`)
			}
			return businessEntityDictionarySignal.value[parentId]
		}
	)

	const renameGroup = async () => {
		const name = groupNameSignal.value
		let group = abridgedClone(groupSignal.value, ["debugInfo", "merchantCount", "name", "properties", "roleCount", "userCount"])
		group.name = name
		isUpdatingSignal.value = true
		try {
			await updateGroup(groupSignal.value.id, group, config, accessToken)
			window.location.reload()
		} catch (error) {
			notifyException(error, "Error renaming group")
			isUpdatingSignal.value = false
		}
	}

	if (businessEntitiesLoadedSignal.value) {
		if (!currentBusinessEntitySignal.value) {
			throw Error("Business entities are loaded, but current business entity is undefined")
		}
	}

	return (<>
		<Breadcrumb className="px-3 pt-1 bg-light">
			<Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
			<Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/groups` }}>Groups</Breadcrumb.Item>
			<Breadcrumb.Item active>{groupLoadedSignal.value ? groupSignal.value.name : "Loading..."}</Breadcrumb.Item>
		</Breadcrumb>
		<PageTitle title='Groups' canEditSignal={canEditComputed} />
		<AdministrationTabs activeKey="2"></AdministrationTabs>
		<Stack direction="horizontal" gap={3}>
			<h1 className="display-1 fs-2 px-3">{groupLoadedSignal.value ? <>Details for group &quot;{groupSignal.value.name}&quot;</> : "Loading..."}</h1>
		</Stack>
		{
			userPermissionsSummary.value.canViewGroups ? (<>
				<div className='panel-body'>
					<Form className="form-horizontal">
						<Container>
							<Row className="mb-2">
								<Col style={{flex:"0 0 50px"}}>
									<label>Name</label>
								</Col>
								<Col>
									<Form.Control
										type="text"
										plaintext={!userPermissionsSummary?.value?.canEditGroups}
										defaultValue={groupNameSignal.value}
										disabled={!groupLoadedSignal.value || !canEditComputed.value}
										spellCheck={false}
										maxLength={50}
										required={true}
										onChange={handleGroupNameChange}
									/>
								</Col>
								<Col>
									<Loadable loadedSignals={[groupLoadedSignal]} errorSignals={[groupErrorSignal]}>
										<Button onClick={renameGroup} disabled={!enableRenameGroupComputed.value}>{isUpdatingSignal.value ? "Renaming..." : "Rename Group"}</Button>
									</Loadable>
								</Col>
							</Row>
							<Row>
								<Col style={{ flex: "0 0 50px" }}>
									<label>Parent</label>
								</Col>
								<Col>
									<Form.Control type="text" value={businessEntitiesLoadedSignal.value && groupLoadedSignal.value ? parentBusinessEntityComputed.value.name : "Loading..."} disabled={true} spellCheck={false} />
								</Col>
								<Col>
									{
										userPermissionsSummary.value.canEditGroups ? (
											<Loadable loadedSignals={[groupLoadedSignal, groupUserListLoadedSignal, groupRoleBusinessEntitiesLoadedSignal]} errorSignals={[groupErrorSignal, groupUserListErrorSignal, groupRoleBusinessEntitiesErrorSignal]}>{
												hasUsersOrBEsComputed.value ? (
													<label className='control-label edit-label' style={{ width: "16px" }} disabled>
														<OverlayTrigger
															placement="right"
															overlay={renderTooltip}
														>
															<Icon icon="no-pencil" height={16} width={16} />
														</OverlayTrigger>
													</label>
												) : (
													<label className="edit-label control-label" style={{ width: "16px" }} onClick={showChangeParentModal}>
															<Icon icon="pencil" height={16} width={16} />
													</label>
												)
											}</Loadable>
										) : (<span>Component locked: You do not have permission to edit groups</span>)
									}
								</Col>
							</Row>
						</Container>
					</Form>
					{
						userPermissionsSummary.value.canViewRoles ? (
							<div className='panel panel-transparent col-xs-12'>
								<header className='panel-heading panelBorder'>
									<h2 className='panel-title float-start'>Roles</h2>
									<span className='panel-actions float-end'>{
										userPermissionsSummary.value.canEditGroups ? (
											<Button onClick={showAddRolesModal}>
												<Icon icon="plus" color="white"></Icon>&nbsp;Add Roles
											</Button>
										) : (
											<span>Component locked: You do not have permission to edit groups</span>
										)
									}</span>
								</header>
								<div className='panel-body'>
									<Table className="table table-striped">
										<thead>
											<tr>
												<th>Name</th>
												<th>Accounts</th>
												<th>Added</th>
												<th>Disassociate</th>
											</tr>
										</thead>
										<tbody className="table-responsive">
											<RoleBusinessEntitiesList roleBusinessEntities={groupRoleBusinessEntitiesSignal.value}></RoleBusinessEntitiesList>
										</tbody>
									</Table>
								</div>
							</div>
						) : (
							<span>Component locked: You do not have permission to view roles</span>
						)
					}
					{
						userPermissionsSummary.value.canViewUsers ? (
							<div className='panel panel-transparent col-xs-12'>
								<header className='panel-heading panelBorder'>
									<h4 className='panel-title float-start'>Users</h4>
									<span className='panel-actions float-end'>{
										userPermissionsSummary.value.canEditGroups ? (
											<Button onClick={showAddUsersModal}>
												<Icon icon="plus" color="white"></Icon>&nbsp;Add Users
											</Button>
										) : (
											<span>Component locked: You do not have permission to edit groups.</span>
										)
									}</span>
								</header>
								<div className='panel-body'>
									<Table className="table table-striped noPointer">
										<thead>
											<tr>
												<th className='hidden-xs'>Username</th>
												<th>First Name</th>
												<th>Last Name</th>
												<th className='hidden-xs'>Email Address</th>
												<th>Parent</th>
												<th className='hidden-xs'>User Status</th>
												<th className='hidden-xs'>Last Login</th>
												<th>Remove</th>
											</tr>
										</thead>
										<tbody className="table-responsive">
											<GroupUsersElement users={groupUserListSignal.value}></GroupUsersElement>
										</tbody>
									</Table>
								</div>
							</div>
						) : (
							<span>Component locked: You do not have permission to view users.</span>
						)
					}
				</div>
				{
					businessEntitiesLoadedSignal.value && (
						<>
							{
								groupLoadedSignal.value && (
									<ChangeParentModal
										showSignal={showChangeParentModalSignal}
										parentBusinessEntityComputed={parentBusinessEntityComputed}
										rootBusinessEntity={rootBusinessEntitySignal.value}
									/>
								)
							}
							<AddGroupRolesModal
								showSignal={showAddRolesModalSignal}
								parentBusinessEntity={currentBusinessEntitySignal.value}
							/>
						</>
					)
				}
				<AddGroupUsersModal
					showSignal={showAddUsersModalSignal}
					userListSignal={userListSignal}
				></AddGroupUsersModal>

				<ConfirmationModal
					show={showDeleteRoleBusinessEntityModalSignal.value}
					handleCancel={
						() => {
							showDeleteRoleBusinessEntityModalSignal.value = false
							roleBusinessEntitySignal.value = null
						}
					}
					handleConfirm={
						() => {
							showDeleteRoleBusinessEntityModalSignal.value = false
							const roleId = roleBusinessEntitySignal.value.roleId
							roleBusinessEntitySignal.value = null
							deleteGroupRoleBusinessEntity(beId, groupId, roleId, config, accessToken).then(
								() => {
									addNotification(`Successfully disassociated group from role`, NotificationTypes.SUCCESS, "Disaccociated")
									scrollToTop("main")
								}
							)
						}
					}
					message={"Are you sure you want to disassociate the selected role and business entities in the current group?"}
					title="Delete group role"
					confirmText='Confirm deletion'
					cancelText='Cancel'
				></ConfirmationModal>

				<ConfirmationModal
					show={showRemoveUserModalSignal.value}
					handleCancel={
						() => {
							showRemoveUserModalSignal.value = false
							groupUserSignal.value = null
						}
					}
					handleConfirm={
						() => {
							showRemoveUserModalSignal.value = false
							const name = groupUserSignal.value.username
							const userId = groupUserSignal.value.id
							groupUserSignal.value = null
							removeUserFromGroup(userId, groupId, config, accessToken).then(
								() => {
									addNotification(`Sucessfully removed user "${name}" from this group`, NotificationTypes.SUCCESS, true, "User Removed from Group")
									scrollToTop("main")
								}
							)
						}
					}
					message={"Remove user \"".concat(groupUserSignal.value ? groupUserSignal.value.username : "Error").concat("\" from this group?")}
					title="User removal confirmation"
					confirmText="Remove user"
					cancelText="Cancel"
				></ConfirmationModal>
			</>) : (
				<AccessDeniedElement></AccessDeniedElement>
			)
		}
	</>)
}
export default GroupDetailPage